import axios from 'axios';
import {
  EDIT_ENTITY_NAME_URL,
  GET_APPLICATION_STATUS,
  GET_ASSIGNED_WIDGETS,
  GET_TABS,
} from '@/utils/constants/constants';
import { resolve_entity_overview_project_id } from './entity_application_overview_config';

const TAB_ICONS = {
  personal_information: 'bi bi-person',
  contact_details: 'bi bi-telephone',
  employment: 'bi bi-briefcase',
  banking_information: 'bi bi-bank',
  Verification: 'bi bi-shield-check',
  Overview: 'bi bi-grid',
  attributes: 'bi bi-sliders',
  Journal: 'bi bi-journal-text',
  Requests: 'bi bi-inbox',
  'Asset Registry': 'bi bi-box-seam',
  'E-Signing': 'fa-solid fa-file-contract'
};

const ENTITY_OVERVIEW_CONTEXT_MAP_KEY = 'entity_application_overview_context_map';
const ENTITY_OVERVIEW_ROUTE_KEYS = [
  'application_id',
  'profile_role_form_id',
  'form_main_section_id',
  'entity_profile_id',
  'user_id',
  'entity_type_id',
];

function build_widget_tabs(data) {
  return data.map(item => ({
    id: item.widget.widget_name,
    label: item.widget.widget_display_name,
    has_dropdown: true,
  }));
}

function build_main_section_tabs(rows) {
  return rows.map(tab => ({
    id: `ms-${tab.id}`,
    label: tab.name,
    _fms_id: tab.id,
  }));
}

function build_current_entity(vm) {
  const context = resolve_current_context(vm);

  return {
    id: vm.entity_profile_id,
    entity_profile_id: vm.entity_profile_id,
    application_id: vm.application_id,
    type_id: vm.entity_type_id,
    entity_type_name: context.entity_type_name || 'Entity',
    entity_name: context.entity_name || vm.entity_name,
    is_child: false
  };
}

function build_context_cache_key(application_id, entity_profile_id) {
  return `${application_id || 'none'}:${entity_profile_id || 'none'}`;
}

function read_context_map() {
  try {
    const raw = sessionStorage.getItem(ENTITY_OVERVIEW_CONTEXT_MAP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function write_context_map(context_map) {
  sessionStorage.setItem(ENTITY_OVERVIEW_CONTEXT_MAP_KEY, JSON.stringify(context_map));
}

function get_route_query(vm) {
  const route_query = vm.$route?.query || {};

  return route_query;
}

function get_route_query_value(vm, key) {
  const route_query = get_route_query(vm);
  const query_value = Reflect.get(route_query, key);

  return query_value ?? null;
}

function get_route_context_value(vm, key, fallback_value = null) {
  const route_value = get_route_query_value(vm, key);

  return route_value ?? fallback_value;
}

function build_dropdown_options_copy(dropdown_options) {
  const dropdown_options_copy =
    typeof dropdown_options === "object" && dropdown_options !== null
      ? { ...dropdown_options }
      : {};

  return dropdown_options_copy;
}

function build_route_context(vm) {
  const route_context = {
    application_id: get_route_context_value(vm, 'application_id', vm.application_id),
    entity_profile_id: get_route_context_value(vm, 'entity_profile_id', vm.entity_profile_id),
    entity_type_id: get_route_context_value(vm, 'entity_type_id', vm.entity_type_id),
    entity_name: get_route_query_value(vm, 'entity_name'),
    entity_type_name: get_route_query_value(vm, 'entity_type_name'),
    status: get_route_query_value(vm, 'status'),
  };

  return route_context;
}

function build_fallback_context(vm) {
  return {
    entity_name: sessionStorage.getItem('entity_name') || vm.entity_name,
    entity_type_name: sessionStorage.getItem('entity_type_name') || 'Entity',
    status: sessionStorage.getItem('application_status') || null,
  };
}

function resolve_current_context(vm) {
  const route_context = build_route_context(vm);
  const context_map = read_context_map();
  const cache_key = build_context_cache_key(route_context.application_id, route_context.entity_profile_id);
  const cached_context = Reflect.get(context_map, cache_key) || {};
  const fallback_context = build_fallback_context(vm);

  return {
    ...fallback_context,
    ...cached_context,
    ...Object.fromEntries(Object.entries(route_context).filter(([, value]) => value != null && value !== "")),
  };
}

function persist_context_to_session(context) {
  const keys = {
    application_id: context.application_id,
    entity_profile_id: context.entity_profile_id,
    entity_type_id: context.entity_type_id,
    entity_name: context.entity_name,
    entity_type_name: context.entity_type_name,
    application_status: context.status,
  };

  Object.entries(keys).forEach(([key, value]) => {
    if (value != null && value !== "") {
      sessionStorage.setItem(key, String(value));
    }
  });
}

export const entity_application_overview_core_methods = {
  resolve_project_id() {
    return resolve_entity_overview_project_id(this);
  },

  async initialize_overview_data() {
    this.set_route_scoped_state();
    this.reset_overview_state();
    await this.load_overview_dependencies();
    this.current_entity = build_current_entity(this);
    this.cache_current_context();
    this.restore_saved_tabs();
  },

  set_route_scoped_state() {
    const current_context = resolve_current_context(this);
    this.project_id = this.resolve_project_id();
    this.entity_name = current_context.entity_name || this.entity_name;
    persist_context_to_session(current_context);
  },

  reset_overview_state() {
    this.visible_tabs = [];
    this.db_tabs = [];
    this.all_tabs = [];
    this.child_tabs = [];
    this.child_entity_tab_map = {};
    this.dropdown_options = {};
    this.clear_folder_list_data();
  },

  async load_overview_dependencies() {
    await this.fetch_assigned_widgets();
    await this.fetch_main_sections_tabs();
    await this.inject_child_entity_type_tabs();
    await this.load_full_hierarchy();
  },

  restore_saved_tabs() {
    const saved_master_tab = sessionStorage.getItem('active_master_tab');
    const saved_tab = sessionStorage.getItem('active_tab');

    this.active_master_tab = saved_master_tab || 'profile';
    if (saved_master_tab) {
      sessionStorage.removeItem('active_master_tab');
    }

    if (this.is_saved_tab_valid(saved_tab)) {
      this.active_tab = saved_tab;
      return;
    }

    if (this.visible_tabs.length > 0) {
      this.select_tab(this.visible_tabs[0].id);
      return;
    }

    this.active_tab = 'Overview';
  },

  is_saved_tab_valid(saved_tab) {
    if (!saved_tab) {
      return false;
    }

    return !this.is_main_sections_placeholder({ id: saved_tab, label: saved_tab }) &&
      !this.is_sub_entities_placeholder({ id: saved_tab, label: saved_tab }) &&
      this.visible_tabs.some(tab => tab.id === saved_tab);
  },

  cache_current_context() {
    const route_context = build_route_context(this);
    const cache_key = build_context_cache_key(route_context.application_id, route_context.entity_profile_id);
    const context_map = read_context_map();

    const existing_context = Reflect.get(context_map, cache_key) || {};
    const current_context = resolve_current_context(this);

    Reflect.set(context_map, cache_key, {
      ...existing_context,
      ...current_context,
      entity_name: this.entity_name,
      entity_type_name: this.current_entity?.entity_type_name || current_context.entity_type_name,
      status: this.application_status || current_context.status,
    });

    write_context_map(context_map);
  },

  async reload_component_data() {
    await this.initialize_overview_data();
    this.get_application_status();
  },

  get_tab_icon(tab_id) {
    return Reflect.get(TAB_ICONS, tab_id) || 'bi bi-folder';
  },

  select_master_tab(master_id) {
    this.active_master_tab = master_id;
    if (this.filtered_sub_tabs.length > 0) {
      this.select_tab(this.filtered_sub_tabs[0].id);
    }
  },

  get_id_from_route_or_storage(name) {
    const from_route = this.get_value_from_route(name);
    const from_storage = this.get_value_from_session(name);
    const raw = from_route ?? from_storage ?? null;

    if (raw == null || raw === "") {
      return null;
    }

    const number_value = Number(raw);
    return Number.isNaN(number_value) ? raw : number_value;
  },

  get_value_from_route(name) {
    if (!ENTITY_OVERVIEW_ROUTE_KEYS.includes(name)) {
      return null;
    }

    const route_value = get_route_query_value(this, name);

    return route_value;
  },

  get_value_from_session(name) {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return null;
      }

      const session_value = window.sessionStorage.getItem(name);

      return session_value;
    } catch {
      return null;
    }
  },

  save_edited_entity() {
    const payload = {
      entity_id: this.entity_profile_id,
      name: this.current_edit_entity.name
    };

    return axios.post(EDIT_ENTITY_NAME_URL, payload, { headers: this.headers })
      .then(() => {
        this.entity_name = payload.name;
        sessionStorage.setItem("entity_name", payload.name);
        this.show_edit_entity_modal = false;
        this.show_success("Entity name updated successfully.");
      })
      .catch(error => {
        const message = error?.response?.data?.message || error.message || "Failed to update entity name.";
        this.show_error(message);
      });
  },

  open_edit_entity_modal() {
    this.current_edit_entity.name = this.entity_name || "";
    this.show_edit_entity_modal = true;
  },

  select_tab(tab_id) {
    this.active_tab = tab_id;
    sessionStorage.setItem('active_tab', tab_id);

    if (this.is_child_entity_type_tab(tab_id)) {
      this.ensure_root_children_loaded();
    }
  },

  auto_set_master_tab(sub_tab_id) {
    if (sub_tab_id === 'Documents') {
      this.active_master_tab = 'documents';
      return;
    }

    this.active_master_tab = sub_tab_id.toLowerCase().includes('compliance') ? 'compliance' : 'profile';
  },

  is_db_main_section_tab(tab_id) {
    return typeof tab_id === 'string' && tab_id.startsWith('ms-');
  },

  dropdown_forms_id(tab_id) {
    if (!this.is_db_main_section_tab(tab_id)) {
      return null;
    }

    return Number(tab_id.split('-')[1]);
  },

  tab_label(tab_id) {
    const all_tabs = [...this.visible_tabs, ...this.db_tabs];
    return all_tabs.find(tab => tab.id === tab_id)?.label || 'Form';
  },

  merge_widget_tabs(tabs) {
    const existing_tabs = Array.isArray(this.visible_tabs) ? this.visible_tabs : [];
    const preserved_tabs = existing_tabs.filter(tab =>
      this.is_db_main_section_tab(tab?.id) || this.is_child_entity_type_tab(tab?.id)
    );

    this.visible_tabs = [...tabs, ...preserved_tabs];
  },

  fetch_assigned_widgets() {
    const request_params = { entity_profile_id: this.entity_profile_id };

    return axios.get(GET_ASSIGNED_WIDGETS, {
      params: request_params,
      headers: this.headers,
    }).then(({ data }) => {
      const tabs = build_widget_tabs(data);
      this.all_tabs = tabs;
      this.merge_widget_dropdown_options(data);
      this.merge_widget_tabs(tabs);
    }).catch(error => {
      const message = error?.response?.data?.message || error.message || "Failed to load widgets";
      this.show_error(message);
    });
  },

  merge_widget_dropdown_options(data) {
    const merged_options = build_dropdown_options_copy(this.dropdown_options);

    data.forEach(item => {
      merged_options[item.widget.widget_name] = [
        { value: item.widget.widget_name, label: item.widget.widget_display_name }
      ];
    });

    this.dropdown_options = merged_options;
  },

  normalize_tab_key(value) {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_");
  },

  is_sub_entities_placeholder(tab) {
    const id = this.normalize_tab_key(tab?.id);
    const label = this.normalize_tab_key(tab?.label);
    return id === "sub_entities" || label === "sub_entities" || label === "sub_entities_widget";
  },

  is_main_sections_placeholder(tab) {
    const id = this.normalize_tab_key(tab?.id);
    const label = this.normalize_tab_key(tab?.label);
    return id === "main_sections" || label === "main_sections" || id === "application_forms" || label === "application_forms";
  },

  fetch_main_sections_tabs() {
    const params = {
      application_id: this.application_id,
      entity_profile_id: this.entity_profile_id,
    };

    return axios.get(GET_TABS, { params, headers: this.headers })
      .then(response => this.apply_main_section_tabs(response))
      .catch(error => {
        this.show_error?.(error?.response?.data?.message || error.message || 'Failed to load tabs');
      });
  },

  apply_main_section_tabs(response) {
    this.application_form_id = response.data.data.application_form_id;
    const rows = response?.data?.data?.data || [];
    const dynamic_tabs = build_main_section_tabs(rows);
    const current_tabs = Array.isArray(this.visible_tabs) ? [...this.visible_tabs] : [];

    this.db_tabs = dynamic_tabs;
    this.visible_tabs = current_tabs.filter(tab =>
      !this.is_db_main_section_tab(tab?.id) && !this.is_main_sections_placeholder(tab)
    );

    this.insert_main_section_tabs(dynamic_tabs, current_tabs);
  },

  insert_main_section_tabs(dynamic_tabs, current_tabs) {
    const placeholder_index = current_tabs.findIndex(tab => this.is_main_sections_placeholder(tab));
    const existing_index = current_tabs.findIndex(tab => this.is_db_main_section_tab(tab?.id));
    const application_forms_index = this.visible_tabs.findIndex(tab => this.normalize_tab_key(tab?.id) === "application_forms");

    if (placeholder_index !== -1) {
      this.visible_tabs.splice(Math.min(placeholder_index, this.visible_tabs.length), 0, ...dynamic_tabs);
      return;
    }

    if (existing_index !== -1) {
      this.visible_tabs.splice(Math.min(existing_index, this.visible_tabs.length), 0, ...dynamic_tabs);
      return;
    }

    if (application_forms_index !== -1) {
      this.visible_tabs.splice(application_forms_index + 1, 0, ...dynamic_tabs);
      return;
    }

    this.visible_tabs.push(...dynamic_tabs);
  },

  get_application_status() {
    const params = { application_id: this.application_id };

    return axios.get(GET_APPLICATION_STATUS, { params, headers: this.headers })
      .then(response => {
        this.application_status = response.data.application_status;
      })
      .catch(() => {
        this.show_error?.('Failed to retrieve application status');
      });
  },
};
